/**
 * Remembers the affiliate code a visitor arrived with (?aff=CODE) for 30 days,
 * so it is still applied at checkout when they come back later. Also mirrors it
 * into sessionStorage['affiliate_code'], which older code (price display) reads.
 */
const KEY = 'nf_affiliate_ref';
const SESSION_KEY = 'affiliate_code';
const DISMISSED_KEY = 'nf_affiliate_ref_dismissed';
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

const safe = <T,>(fn: () => T, fallback: T): T => {
  try { return fn(); } catch { return fallback; }
};

export const saveAffiliateRef = (code: string) => {
  const clean = code.trim().toUpperCase();
  if (!clean) return;
  safe(() => localStorage.setItem(KEY, JSON.stringify({ code: clean, ts: Date.now() })), undefined);
  safe(() => sessionStorage.setItem(SESSION_KEY, clean), undefined);
  safe(() => sessionStorage.removeItem(DISMISSED_KEY), undefined);
};

export const getAffiliateRef = (): string | null => {
  const raw = safe(() => localStorage.getItem(KEY), null);
  if (raw) {
    const parsed = safe(() => JSON.parse(raw) as { code?: string; ts?: number }, null);
    if (parsed?.code && parsed.ts && Date.now() - parsed.ts < TTL_MS) {
      safe(() => sessionStorage.setItem(SESSION_KEY, parsed.code!), undefined);
      return parsed.code;
    }
    safe(() => localStorage.removeItem(KEY), undefined);
  }
  return safe(() => sessionStorage.getItem(SESSION_KEY), null);
};

/** The buyer removed the auto-applied code in the cart — don't re-apply it this session. */
export const dismissAffiliateRef = () => safe(() => sessionStorage.setItem(DISMISSED_KEY, '1'), undefined);
export const isAffiliateRefDismissed = () => safe(() => sessionStorage.getItem(DISMISSED_KEY) === '1', false);
